import { storefrontClient } from "@merchstores/frontend/App";
import * as storefrontQuery from "@merchstores/frontend/queries/storefront-graphql";

import { ICart, ICartItem, IMerchStore } from '@merchstores/frontend/reduxTypes';
import { OrderType, CLOSE_DATE_PENDING } from '@merchstores/shared/components/MerchStore';

const SHOPIFY_CHECKOUT_DOMAIN = process.env.REACT_APP_SHOPIFY_CHECKOUT_DOMAIN;

export interface ICustomAttribute
{
  key: string, 
  value: string
}

export interface ILineItem {
  variantId: string,
  quantity: number,
  customAttributes: Array<ICustomAttribute>
}

export interface IOfficeAddress {
  firstName: string,
  lastName: string,
  address1: string,
  address2?: string,
  city: string,
  company: string,
  zip: string,
  province: string,
  country: string
}

export interface IMailingAddressInput {
  firstName: string,
  lastName: string,
  address1: string,
  address2?: string,
  city: string,
  company: string,
  zip: string,
  province: string,
  country: string
}

export interface ICheckoutFields 
{
  lineItems: Array<ILineItem>,
  customAttributes: Array<ICustomAttribute>,
  shippingAddress?: IMailingAddressInput
}

export function customAttr(attrName: string, attrValue: string) : ICustomAttribute
{
  return { key: attrName, value: attrValue };
}

export function buildLineItemAttributes(item: ICartItem, merchstore: IMerchStore) : Array<ICustomAttribute>
{
  let attributes: Array<ICustomAttribute> = [];

  attributes = attributes.concat(
    customAttr('Decoration type', item.decorationType || ''),
    customAttr('Select decoration location', item.decorationLocation || ''),
    customAttr('Upload your logo (2MB max)', item.artwork || ''),
    customAttr('Logo Mockup Image', item.logoMockup || '')
  );

  return attributes.filter(attr => typeof attr.value !== 'undefined');
}

export function buildCheckoutFields(cart: ICart, merchstore: IMerchStore) : ICheckoutFields
{
  const cartItems: Array<ICartItem> = cart.items;

  const baseLineItemAttributes: Array<ICustomAttribute> = [];

  const lineItems = cartItems.map(item => {
    const lineItemAttributes = baseLineItemAttributes.concat(
      buildLineItemAttributes(item, merchstore)
    );

    return {
      variantId: item.id,
      quantity: item.quantity,
      customAttributes: lineItemAttributes
    }
  });

  const checkoutCustomizationAttributes = [
    customAttr('payer_type', merchstore.payerType || ''),
    customAttr('order_type', OrderType.INDIVIDUAL),
    customAttr('ship_to_office', merchstore.shipToOffice ? "true" : "false")
  ];

  if (lineItems.length)
  {
    lineItems[0].customAttributes = lineItems[0].customAttributes.concat(checkoutCustomizationAttributes);
  }

  if (lineItems.length > 1)
  {
    const lastElem= lineItems.length - 1;
    lineItems[lastElem].customAttributes = lineItems[lastElem].customAttributes.concat(checkoutCustomizationAttributes);
  }

  const cartAttributes: Array<ICustomAttribute> = [
    customAttr('subdomain', merchstore.subdomain),
    customAttr('merchstore_code', merchstore.storeCode + ''),
    customAttr('payer_type', merchstore.payerType + ''),
    customAttr('order_type', OrderType.INDIVIDUAL),
    customAttr('ship_to_office', merchstore.shipToOffice + ''),
    customAttr('close_date', CLOSE_DATE_PENDING),
    customAttr('merchstore_logo', merchstore.storeLogo + ''),
    
  ].filter(attr => typeof attr.value !== 'undefined');

  const checkoutFields: ICheckoutFields = {
    lineItems,
    customAttributes: cartAttributes
  }
 
  if (merchstore.shipToOffice && merchstore.officeAddress) {
    checkoutFields.shippingAddress = {
      company: merchstore.officeAddress.company,
      firstName: merchstore.officeAddress.firstName,
      lastName: merchstore.officeAddress.lastName,
      address1: merchstore.officeAddress.address1,
      address2: merchstore.officeAddress.address2,
      province: merchstore.officeAddress.province,
      zip: merchstore.officeAddress.zip,
      city: merchstore.officeAddress.city,
      country: merchstore.officeAddress.country,
    }
  }

  return checkoutFields;
}

interface IErrorInvalidItem { 
  code: string, 
  variantId: string 
}

export async function extractCheckoutErrors(
  checkoutErrors: Array<{code: string}>, 
  checkoutFields: ICheckoutFields
): Promise<Array<IErrorInvalidItem>>
{
  const errors: Array<IErrorInvalidItem> = [];

  const invalidErrors = checkoutErrors.filter( (errorItem) => errorItem.code === 'INVALID');

  if (!invalidErrors.length)
  {
    return errors;
  }
  
  const invalidItemErrorDetails = await Promise.all(checkoutFields.lineItems.map(lineItem => {
    const errorCode = 'INVALID';
    const productVariantQuery = storefrontQuery.getProductVariantByIdQuery(lineItem.variantId);

    const checkErrorPromise = new Promise((resolve) => {
      storefrontClient.send(productVariantQuery).then( ( {data}: { data: {node: unknown} }) => {
        if (!data.node) {
          return resolve({ code: errorCode, variantId: lineItem.variantId });
        }
        resolve(null);
      });
    });

    return checkErrorPromise as Promise<IErrorInvalidItem|null>;
  }));

  invalidItemErrorDetails.filter(Boolean).forEach( (errorItem)  =>
  {
    errorItem = errorItem as IErrorInvalidItem;
    errors.push({ code: errorItem.code, variantId: errorItem.variantId });
  });

  return errors;
}

export interface ICheckoutStatus {
  checkoutUrl: string,
  errors: Array<IErrorInvalidItem>
}

export async function requestCheckoutUrl (checkoutFields: ICheckoutFields): Promise<ICheckoutStatus>
{
  const checkoutCreateMutation = storefrontQuery.getCheckoutCreateMutation(checkoutFields);

  const createResponse = await storefrontClient.send(checkoutCreateMutation);

  if (createResponse.errors)
  {
    throw new Error(JSON.stringify(createResponse.errors));
  }

  const createData = createResponse.data.checkoutCreate;

  const createErrors = createData.checkoutUserErrors;

  const errors = await extractCheckoutErrors(createErrors, checkoutFields);

  const checkoutData = createData.checkout;
  let checkoutUrl = checkoutData ? checkoutData.webUrl : '';
  
  if (SHOPIFY_CHECKOUT_DOMAIN)
  {
    checkoutUrl = checkoutUrl.replace(/https:\/\/[^.]+\.myshopify\.com\//, `https://${SHOPIFY_CHECKOUT_DOMAIN}/`)
  }

  return {
    checkoutUrl: checkoutUrl,
    errors: errors
  }
}
